import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { computeLayout } from '../../services/legacyChainEngine';

const LINK_COLORS = {
  spouse:        '#C9A84C',
  parent:        '#1E3A5F',
  sibling:       '#7C3AED',
  child:         '#059669',
  cross_surname: '#F59E0B',
};

const LINK_DASH = {
  cross_surname: '6 4',
};

const NODE_R = 26;

function CurvedEdge({ edge, posA, posB }) {
  if (!posA || !posB) return null;
  const mx = (posA.x + posB.x) / 2;
  const my = (posA.y + posB.y) / 2 - (edge.crossMemorial ? 60 : 20);
  const color = LINK_COLORS[edge.type] || '#94A3B8';
  const dash  = LINK_DASH[edge.type] || '';
  const opacity = edge.crossMemorial ? 0.55 : 0.4;

  return (
    <path
      d={`M ${posA.x} ${posA.y} Q ${mx} ${my} ${posB.x} ${posB.y}`}
      fill="none"
      stroke={color}
      strokeWidth={edge.crossMemorial ? 2 : 1.5}
      strokeDasharray={dash}
      opacity={opacity}
    />
  );
}

function PersonNode({ node, pos, isSelected, isHighlighted, isDimmed, onClick }) {
  if (!pos) return null;
  const r = NODE_R + (isSelected ? 4 : 0);
  const opacity = isDimmed ? 0.25 : 1;

  return (
    <g
      transform={`translate(${pos.x},${pos.y})`}
      onClick={() => onClick(node)}
      style={{ cursor: 'pointer', opacity }}
    >
      {isSelected && (
        <circle r={r + 6} fill="none" stroke={node.accentColor} strokeWidth={2.5} opacity={0.7}>
          <animate attributeName="r" values={`${r + 5};${r + 10};${r + 5}`} dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="2s" repeatCount="indefinite" />
        </circle>
      )}
      {isHighlighted && !isSelected && (
        <circle r={r + 4} fill={node.lightColor} stroke={node.color} strokeWidth={1.5} opacity={0.6} />
      )}

      {/* Avatar or initials */}
      <clipPath id={`clip-${node.id.replace(/:/g, '-')}`}>
        <circle r={r} />
      </clipPath>
      {node.avatar ? (
        <image
          href={node.avatar}
          x={-r} y={-r}
          width={r * 2} height={r * 2}
          clipPath={`url(#clip-${node.id.replace(/:/g, '-')})`}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <>
          <circle r={r} fill={node.color} />
          <text textAnchor="middle" dominantBaseline="central"
            fontSize={r * 0.6} fill="white" fontWeight="900" fontFamily="serif">
            {node.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </text>
        </>
      )}

      {/* Border */}
      <circle r={r} fill="none"
        stroke={isSelected ? node.accentColor : node.color}
        strokeWidth={isSelected ? 3 : 1.5} />

      {/* Deceased candle */}
      {node.isDeceased && (
        <text x={r - 4} y={-r + 6} fontSize={10} textAnchor="middle">🕯</text>
      )}

      {/* Name label */}
      <text textAnchor="middle" y={r + 14} fontSize={8.5} fill="#1E3A5F"
        fontWeight="600" fontFamily="system-ui" style={{ pointerEvents: 'none' }}>
        {node.name.split(' ')[0]}
      </text>
    </g>
  );
}

export default function LegacyChainCanvas({ nodes, edges, memorialClusters, onSelectNode, selectedNode }) {
  const svgRef  = useRef(null);
  const [pan,   setPan]  = useState({ x: 0, y: 0 });
  const [zoom,  setZoom] = useState(0.85);
  const dragging = useRef(false);
  const lastPos  = useRef({ x: 0, y: 0 });
  const W = 900, H = 680;

  const positions = useMemo(
    () => computeLayout(nodes, memorialClusters, W, H),
    [nodes, memorialClusters]
  );

  const connectedIds = useMemo(() => {
    if (!selectedNode) return null;
    return new Set(
      edges
        .filter(e => e.source === selectedNode.id || e.target === selectedNode.id)
        .flatMap(e => [e.source, e.target])
    );
  }, [selectedNode, edges]);

  const onMouseDown = useCallback(e => {
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onMouseMove = useCallback(e => {
    if (!dragging.current) return;
    setPan(p => ({ x: p.x + e.clientX - lastPos.current.x, y: p.y + e.clientY - lastPos.current.y }));
    lastPos.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  const onWheel = useCallback(e => {
    e.preventDefault();
    setZoom(z => Math.min(2.5, Math.max(0.3, z - e.deltaY * 0.001)));
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  return (
    <div className="relative w-full overflow-hidden rounded-[32px] border border-brand-dark bg-gradient-to-br from-[#F8F5EE] via-white to-[#EFF4FB] shadow-inner"
      style={{ minHeight: 500, cursor: dragging.current ? 'grabbing' : 'grab' }}>
      <svg ref={svgRef} width="100%" height={560} viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        style={{ userSelect: 'none' }}>

        <defs>
          <pattern id="lcgrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E5D9C3" strokeWidth="0.4" />
          </pattern>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`} style={{ transformOrigin: `${W / 2}px ${H / 2}px` }}>
          {/* Background grid */}
          <rect width={W} height={H} fill="url(#lcgrid)" opacity={0.5} />

          {/* Cluster halos */}
          {memorialClusters.map(cluster => {
            const clusterNodes = nodes.filter(n => cluster.nodeIds.includes(n.id));
            if (!clusterNodes.length) return null;
            const pos0 = positions[clusterNodes[0].id];
            if (!pos0) return null;
            return (
              <circle key={cluster.id}
                cx={pos0.cx} cy={pos0.cy} r={W * 0.13}
                fill={cluster.lightColor} opacity={0.35}
                stroke={cluster.color} strokeWidth={1} strokeDasharray="6 4" />
            );
          })}

          {/* Cluster labels */}
          {memorialClusters.map(cluster => {
            const clusterNodes = nodes.filter(n => cluster.nodeIds.includes(n.id));
            if (!clusterNodes.length) return null;
            const pos0 = positions[clusterNodes[0].id];
            if (!pos0) return null;
            return (
              <text key={`lbl-${cluster.id}`}
                x={pos0.cx} y={pos0.cy - W * 0.14}
                textAnchor="middle" fontSize={10} fontWeight="900"
                fill={cluster.color} fontFamily="Georgia, serif" opacity={0.7}>
                {cluster.name.split(' ').slice(0, 2).join(' ')}
              </text>
            );
          })}

          {/* Edges */}
          {edges.map(edge => (
            <CurvedEdge key={edge.id} edge={edge}
              posA={positions[edge.source]}
              posB={positions[edge.target]} />
          ))}

          {/* Nodes */}
          {nodes.map(node => {
            const isSelected    = selectedNode?.id === node.id;
            const isHighlighted = connectedIds ? connectedIds.has(node.id) : false;
            const isDimmed      = selectedNode ? (!isSelected && !isHighlighted) : false;
            return (
              <PersonNode key={node.id} node={node}
                pos={positions[node.id]}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onClick={onSelectNode} />
            );
          })}
        </g>
      </svg>

      {/* Controls */}
      <div className="absolute bottom-4 left-4 flex flex-col gap-1 z-10">
        {[{ l: '+', d: +0.2 }, { l: '−', d: -0.2 }].map(b => (
          <button key={b.l} onClick={() => setZoom(z => Math.min(2.5, Math.max(0.3, z + b.d)))}
            className="w-8 h-8 bg-white border border-brand-dark rounded-lg text-accent-navy font-black text-base shadow hover:bg-brand-light transition-colors flex items-center justify-center">
            {b.l}
          </button>
        ))}
        <button onClick={() => { setZoom(0.85); setPan({ x: 0, y: 0 }); }}
          className="w-8 h-8 bg-white border border-brand-dark rounded-lg text-accent-navy font-black text-[9px] shadow hover:bg-brand-light transition-colors flex items-center justify-center" title="Reset">
          ⌂
        </button>
      </div>
      <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm border border-brand-dark rounded-xl px-3 py-1.5 text-[9px] font-black text-text-muted uppercase tracking-widest">
        {Math.round(zoom * 100)}%
      </div>
    </div>
  );
}